// src/ui/components/WarmUp.tsx
import React, { useMemo } from 'react';
import parse from 'html-react-parser';
import { removeEmptyTags, sanitizeHtml } from '../../../../core/helpers/htmlSanitizer';
import { extractFirstMedia } from '../../../../core/helpers/extractFirstMedia';
import { createParserOptions } from '../../../../core/helpers/createParserOptions';
import FullscreenDialog from '../../dialogs/FullscreenDialog';
import { Wod } from '../../../../core/models/Wod';

interface WarmUpProps {
  wod: Wod;
  html?: string;
  className?: string;
  showMedia?: boolean;
  showExpanded?: boolean;
  showFullScreen?: boolean;
  useRowLayout?: boolean;
}

const WarmUp: React.FC<WarmUpProps> = ({
  wod,
  html = '',
  className = '',
  showMedia = false,
  showExpanded,
  showFullScreen = false,
  useRowLayout = false,
}) => {
  // 1) Sanitiza el HTML original (incluye media si showMedia=true)
  const sanitizedAllowMedia = useMemo(() => sanitizeHtml(html || '', true), [html]);

  // 2) Extraemos el primer media (video > iframe > img) desde el HTML ya sanitizado (con media permitido)
  const { mediaHtml, contentHtml } = useMemo(
    () => extractFirstMedia(sanitizedAllowMedia, { onlyVideo: false, fallbackToIframe: true }),
    [sanitizedAllowMedia],
  );

  // 3) Vuelve a sanear el contentHtml pero sin permitir media (esto es lo que mostrarás como texto)
  const sanitizedContent = useMemo(() => sanitizeHtml(contentHtml || '', false), [contentHtml]);

  // 4) Limpia tags vacíos del contenido final que se mostrará en la card
  const cleanedWork = useMemo(() => removeEmptyTags(sanitizedContent), [sanitizedContent]);

  // Extraer src de mediaHtml (si es <img ...>)
  const bgImageUrl = useMemo(() => {
    if (!mediaHtml) return '';
    const srcMatch =
      mediaHtml.match(/src\s*=\s*["']([^"']+)["']/i) || mediaHtml.match(/src\s*=\s*([^>\s]+)/i);
    if (srcMatch) return srcMatch[1];
    const dataSrcMatch =
      mediaHtml.match(/data-src\s*=\s*["']([^"']+)["']/i) ||
      mediaHtml.match(/data-src\s*=\s*([^>\s]+)/i);
    if (dataSrcMatch) return dataSrcMatch[1];
    return '';
  }, [mediaHtml]);

  const parserOptions = useMemo(
    () =>
      createParserOptions({
        title: wod.title,
        showMedia: !!showMedia,
        showExpanded,
        showFullScreen,
        useRowLayout: !!useRowLayout,
      }),
    [wod.title, showMedia, showExpanded, showFullScreen, useRowLayout],
  );
  const parserOptionsDialog = useMemo(
    () =>
      createParserOptions({
        title: wod.title,
        showMedia: !!showMedia,
        showExpanded,
        showFullScreen,
        useRowLayout: !!useRowLayout,
      }),
    [wod.title, showMedia, showExpanded, showFullScreen, useRowLayout],
  );

  // Si hay imagen, usamos una capa absoluta para mostrarla con opacidad/blur.
  // Ajusta bgOpacity y bgBlur a tu gusto.
  const bgOpacity = 0.08; // 0 .. 1
  const bgBlur = 2; // px de blur (ej: 0, 1, 2, ...)
  const containerBase = `relative rounded-[24px] p-3 transition hover:shadow-md ${bgImageUrl ? 'overflow-hidden shadow' : 'bg-gray-200/25 shadow'}`;

  return (
    <div className={`text-white ${className}`}>
      <h2 className="text-2xl font-bold mb-4">Warm Up</h2>

      <div className={containerBase} aria-hidden={false}>
        {/* capa blanca de fondo (siempre) */}
        <div
          className="absolute inset-0 rounded-[24px] pointer-events-none bg-gray-100/20"
          style={{ zIndex: 0 }}
          aria-hidden="true"
        />
        {/* capa de fondo con la imagen (no afecta el contenido) */}
        {bgImageUrl ? (
          <div
            className="absolute inset-0 rounded-[24px] bg-center bg-cover bg-no-repeat pointer-events-none"
            style={{
              backgroundImage: `url("${bgImageUrl}")`,
              opacity: bgOpacity,
              filter: `blur(${bgBlur}px) saturate(0.95)`,
            }}
            aria-hidden="true"
          />
        ) : null}

        {/* overlay ligero (opcional): ajusta opacity en bg-black/xx si quieres más contraste) */}
        {bgImageUrl ? (
          <div className="absolute inset-0 rounded-[24px] bg-black/10 pointer-events-none" />
        ) : null}

        {/* contenido en primer plano */}
        <div className="relative z-10 ml-2 prose max-w-none text-white">{parse(cleanedWork, parserOptions)}</div>
      </div>

      <FullscreenDialog
        title={wod.title}
        mediaHtml={mediaHtml}
        sanitizedWork={cleanedWork}
        parserOptions={parserOptionsDialog}
        showExpanded={showExpanded}
        showFullScreen={showFullScreen}
      />
    </div>
  );
};

export default WarmUp;
